// src/components/auth/SignUp/SignUp.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { Steps } from "antd";

import RadioGroup from "@components/forms/RadioGroup";
import FormField from "@components/forms/FormField";
import Input from "@components/forms/Input";
import Choice from "@components/Choice";
import AISelect from "@components/forms/AISelect";
import Modal from "@components/Modal";
import { Col, Row } from "react-bootstrap";


import { useSignUpForm } from "./hooks/useSignUpForm";
import { useSignUpConf } from "./hooks/useSignUpConf";
import { getRoute } from "@services/axios";
import { VALIDATION_RULES } from "./rules/Rules";
import { LucideAsterisk } from "lucide-react";
import Loader from "@layout/Loader";

const SignUp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // États
  const [providerType, setProviderType] = useState("company");
  const [hasWebsite, setHasWebsite] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [step, setStep] = useState(0);
  const [selectCurrency, setSelectCurrency] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dialCode, setDialCode] = useState("+33");
  const [currencyError, setCurrencyError] = useState();
  const [countryError, setCountryError] = useState();
  const [cityError, setCityError] = useState();
  const [phoneError, setPhoneError] = useState();
  // Hooks personnalisés
  const {
    loading: dataLoading,
    countries,
    cities,
    currencies,
    
  } = useSignUpConf(selectedCountry?.value);

  const {
    register,
    handleSubmit,
    errors,
    trigger,
    password,
    setValue,
    clearErrors,
    isSubmitting,
    onSubmit,
  } = useSignUpForm(t);

  // Configuration des steps
  const stepItems = [
    { title: `${t("step")} 1` },
    { title: `${t("step")} 2` },
    { title: `${t("step")} 3` },
  ];

  // Champs par étape
  const stepFields = {
    0: ["provider_type_id", "name", "email"],
    1: ["address", "postal_code", "phone", ...(hasWebsite ? ["website"] : [])],
    2: ["password", "password_confirmation"],
  };

  // Mettre à jour le dial code quand le pays change
  useEffect(() => {
    if (selectedCountry?.dial_code) {
      setDialCode(selectedCountry.dial_code);
    }
  }, [selectedCountry]);

  // Validation et passage à l'étape suivante
  const nextStep = async () => {
    const fields = stepFields[step];
    const valid = await Promise.all(
      fields.map((f) => trigger(f))
    );

    if (valid.every(Boolean)) {
      // Validation spécifique à l'étape 0
      if (step === 0) {
      console.log(selectCurrency)
        if (!selectCurrency?.value) {
          setCurrencyError(t('currency_required'));
          return;
        }
      }

      // Validation spécifique à l'étape 1
      if (step === 1) {
         console.log(selectedCountry)
        if (!selectedCountry?.value) {
          setCountryError(t('select_country'));
          return;
        }
        if (!selectedCity?.value) {
          setCityError(t('select_city'));
          return;
        }
        if (!phoneNumber) {
          setPhoneError(t('phone_required'));
          return;
        }
      }

      setStep(step + 1);
    }
  };

  const prevStep = () => setStep(step - 1);

  // Gestion du succès
  const handleSuccess = () => {
    setShowSuccessModal(true);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/");
  };

  // Options pour le type de fournisseur
  const providerOptions = [
    { value: "1", label: t("Company") },
    // { value: "0", label: t("Individual") },
  ];
  const [namePlaceholder, setNamePlaceholder] = useState(
    providerType === "company" ? t("company_name") : t("full_name")
  );

  // Mettre à jour le placeholder quand providerType change
  useEffect(() => {
    setNamePlaceholder(
      providerType === "1" ? t("company_name") : t("full_name")
    );
  }, [providerType, t]);


  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Set isLoading to true after 300 seconds (300,000 milliseconds)
    const timer = setTimeout(() => {
      setIsLoading(true);
    }, 3000); // 300 seconds = 300,000 ms

    // Cleanup timer if component unmounts
    return () => clearTimeout(timer);
  }, []); // Empty dependency array means this runs once when component mounts
  return (

   <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="">
          <h2 className="mb-4 text-center text-dark">
            {t("registration_form")}
          </h2>

          <Steps
            current={step}
            onChange={(value) => setStep(value)}
            items={stepItems}
            className="mb-4"
          />

          <form
            onSubmit={handleSubmit((data) =>
              onSubmit(data, handleSuccess, selectCurrency, selectedCountry, selectedCity,phoneNumber)
            )}
          >
            {/* Étape 1: Compte */}
            {step === 0 && (
              <>

                <RadioGroup
                  label={t("are_you")}
                  options={providerOptions}
                  name="provider_type_id"
                  register={register}
                  error={errors?.provider_type_id && t("please_choose_one")}
                  onChange={setProviderType}
                  defaultValue="1"
                />

                <div className="d-flex align-items-center pb-2">
                  <FormField error={errors?.name && t("name_required")}  className="flex-grow-1" >
                    <Input
                      className="form-control p-3"
                      placeholder={namePlaceholder}
                      {...register("name", { required: t("name_required") })}
                    />

                  </FormField>
                  <LucideAsterisk size={18} className="text-red" />
                </div>
                <div className="d-flex align-items-center pb-2">
                  <FormField error={errors?.email?.message && t("email_required")} className="flex-grow-1">
                    <Input
                      type="email"
                      className="form-control p-3"
                      placeholder={t("email_address")}
                      {...register("email", { required: t("email_required") })} // Plus de (t)
                    />
                  </FormField>
                  <LucideAsterisk size={18} className="text-red" />
                </div>


                {/* <FormField error={errors?.phone?.message}>
                  <Input
                    type="tel"
                    className="form-control p-3"
                    placeholder={t("phone")}
                    {...register("phone", VALIDATION_RULES.phone)} // Plus de (t)
                  />
                </FormField> */}
                <div className="">
                  <div className="d-flex align-items-center ">
                   <div className="flex-grow-1 fw-light">
                     <AISelect
                      defaultOptionsData={currencies}
                      selectedValue={selectCurrency}
                      setSelectedValue={setSelectCurrency}
                      label={t("choose_currency")}
                      route={getRoute("currencies")}
                      menuPlacement="top"
                      options={currencies}
                      className=""
                    />
                   </div>
                    <LucideAsterisk size={18} className="text-red" />
                  </div>
                  <div className="text-danger d-block mt-1 " style={{ "font-size": "0.875em" }}>{currencyError}</div>

                </div>


              </>
            )}

            {/* Étape 2: Entreprise */}
            {step === 1 && (
              <>
                <Row>
                  <Col md={6}>

                    <div className="d-flex align-items-center w-100">
                      <FormField className="flex-grow-1">
                        <AISelect
                          selectedValue={selectedCountry}
                          setSelectedValue={setSelectedCountry}
                          label={t("country")}
                          route={getRoute("COUNTRIES")}
                          options={countries}
                          defaultOptionsData={countries}
                        />
                      </FormField>
                      <LucideAsterisk size={18} className="text-red" />
                    </div>
                    <div className="text-danger d-block mt-1 " style={{ "font-size": "0.875em" }}>{countryError}</div>



                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center w-100">
                      <FormField className="flex-grow-1">
                        <AISelect
                          selectedValue={selectedCity}
                          setSelectedValue={setSelectedCity}
                          label={t("city")}
                          route={selectedCountry ? `${getRoute("CITIES")}/${selectedCountry.value}` : null}
                          disabled={!selectedCountry}
                          defaultOptionsData={cities}
                        />
                      </FormField>
                      <LucideAsterisk size={18} className="text-red" />
                    </div>
                    <div className="text-danger d-block mt-1 " style={{ "font-size": "0.875em" }}>{cityError}</div>
                  </Col>
                </Row>
                <div className="d-flex align-items-center w-100 py-2">
                  <FormField label={t("phone_number")} error={errors?.phone_number?.message} className="flex-grow-1">
                    <div className="d-flex">
                      <span
                        className="bg-light border px-3 py-2 rounded-start"
                        style={{
                          minWidth: "80px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#f8f9fa",
                          borderRight: "none"
                        }}
                      >
                        {dialCode}
                      </span>
                      <Input
                        type="tel"
                        className="form-control p-3 rounded-start-0"
                        placeholder={t("phone_number")}
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setPhoneNumber(value);
                          setValue("phone_number", value);
                          // Valider automatiquement après le changement
                          trigger("phone_number");
                        }}
                      />
                    </div>
                  </FormField>
                  <LucideAsterisk size={18} className="text-red" />
                </div>
                <div className="text-danger d-block mt-1 " style={{ "font-size": "0.875em" }}>{phoneError}</div>

                <Row>
                  <Col md={8}>
                    <FormField className="flex-grow-1">
                      <Input
                        className="form-control p-3"
                        placeholder={t("full_address")}
                        {...register("address")}
                      />
                    </FormField>
                  </Col>
                  <Col md={4}>
                    <FormField error={errors?.postal_code?.message} className="d-flex align-items-center" >
                      <Input
                        className="form-control p-3"
                        placeholder={t("postal_code")}
                        {...register("postal_code", VALIDATION_RULES.postal_code)} // Plus de (t)
                      />
                      <LucideAsterisk size={18} className="text-red" />
                    </FormField>
                  </Col>
                </Row>

                <Choice
                  text={t("do_you_have_website")}
                  selectedChoice={hasWebsite ? "yes" : "no"}
                  onChange={(value) => {
                    const hasSite = value === "yes";
                    setHasWebsite(hasSite);
                    if (!hasSite) {
                      setValue("website", "");
                      clearErrors("website");
                    }
                  }}
                />

                {hasWebsite && (
                  <FormField error={errors?.website?.message}>
                    <Input
                      className="form-control p-3"
                      placeholder={t("website_example")}
                      {...register("website", VALIDATION_RULES.website)} // Plus de (t)
                    />
                  </FormField>
                )}
              </>
            )}

            {/* Étape 3: Sécurité */}
            {step === 2 && (
              <>
                <FormField error={errors?.password?.message}>
                  <Input
                    type="password"
                    className="form-control p-3"
                    placeholder={t("password")}
                    {...register("password", VALIDATION_RULES.password)} // Plus de (t)
                  />
                </FormField>

                <FormField error={errors?.password_confirmation?.message}>
                  <Input
                    type="password"
                    className="form-control p-3"
                    placeholder={t("confirm_password")}
                    {...register("password_confirmation", {
                      required: t("confirm_password_required"),
                      validate: (value) =>
                        value === password || t("passwords_do_not_match"),
                    })}
                  />
                </FormField>
              </>
            )}

            {/* Boutons de navigation */}
            <div className="d-flex justify-content-between mt-4 gap-2">
              {step > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-secondary p-3 flex-fill"
                  onClick={prevStep}
                >
                  {t("back")}
                </button>
              )}

              {step < 2 && (
                <button
                  type="button"
                  className="btn btn-primary p-3 flex-fill"
                  onClick={nextStep}
                >
                  {t("next")}
                </button>
              )}

              {step === 2 && (
                <button
                  type="submit"
                  className="btn btn-blue p-3 flex-fill"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("submitting") : t("sign_up")}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Modal de succès */}
      <Modal
        open={showSuccessModal}
        onOk={handleCloseModal}
        onCancel={handleCloseModal}
        okText={t("ok")}
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <h4 className="mb-3">{t("registration_successful")}</h4>
        <p>
          {t("registration_success_message")}
        </p>
      </Modal>
    </div>

  );
};

export default SignUp;